import axiosInstance from "../axiosClient";

/**
 * Check organization subscription eligibility
 * Returns user's current subscription status and recommendations
 *
 * @param {string} token - Authentication token
 * @param {string} userId - User ID
 * @returns {Promise} Eligibility data including recommendations
 */
export const checkOrganizationEligibility = async (token, userId) => {
  try {
    const response = await axiosInstance.post(
      `/organization/subscription/eligibility`,
      { userId },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create organization checkout session with preservation option
 *
 * @param {string} token - Authentication token
 * @param {Object} checkoutData - Checkout details
 * @param {string} checkoutData.priceId - Stripe price ID
 * @param {string} checkoutData.userId - User ID
 * @param {string} checkoutData.organizationName - Organization name
 * @param {string} checkoutData.organizationIdentifier - Organization identifier
 * @param {string} checkoutData.email - User email
 * @param {boolean} checkoutData.preserveIndividualSubscription - Whether to preserve individual subscription (default: true)
 * @returns {Promise} Checkout session URL
 */
export const createOrganizationCheckout = async (token, checkoutData) => {
  try {
    const response = await axiosInstance.post(
      `/organization/subscription/checkout`,
      {
        priceId: checkoutData.priceId,
        userId: checkoutData.userId,
        organizationName: checkoutData.organizationName,
        organizationIdentifier: checkoutData.organizationIdentifier,
        email: checkoutData.email,
        preserveIndividualSubscription: checkoutData.preserveIndividualSubscription ?? true // Default to true
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get comprehensive subscription summary
 * Returns all subscriptions (individual + organizations) with total cost
 *
 * @param {string} token - Authentication token
 * @param {string} userId - User ID
 * @returns {Promise} Subscription summary with all subscriptions and features
 */
export const getSubscriptionSummary = async (token, userId) => {
  try {
    const response = await axiosInstance.get(
      `/subscription/summary`,
      {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get feature limits for current context
 *
 * @param {string} userId - User ID
 * @param {number} organizationId - Optional organization ID for org context
 * @returns {Promise} Feature limits and subscription details
 */
export const getFeatureLimits = async (userId, organizationId = null) => {
  try {
    const params = { userId };
    if (organizationId) {
      params.organizationId = organizationId;
    }

    const response = await axiosInstance.get(
      `/subscription/limits`,
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create individual subscription checkout session
 *
 * @param {string} token - Authentication token
 * @param {Object} checkoutData - Checkout details
 * @param {string} checkoutData.priceId - Stripe price ID
 * @param {string} checkoutData.email - User email
 * @returns {Promise} Checkout session URL
 */
export const createIndividualCheckout = async (token, checkoutData) => {
  try {
    const response = await axiosInstance.post(
      `/create-checkout-session`,
      checkoutData,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Open Stripe Customer Portal for individual subscription management
 *
 * @param {string} token - Authentication token
 * @param {string} customerId - Stripe Customer ID
 * @returns {Promise} Customer portal URL
 */
export const openIndividualPortal = async (token, customerId) => {
  try {
    const response = await axiosInstance.post(
      `/create-portal-session`,
      { customerId },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Open Stripe Customer Portal for organization subscription management
 *
 * @param {string} token - Authentication token
 * @param {string} organizationId - Organization UUID
 * @returns {Promise} Customer portal URL
 */
export const openOrganizationPortal = async (token, organizationId) => {
  try {
    const response = await axiosInstance.post(
      `/organization/subscription/portal`,
      { organizationId },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get organization subscription details
 *
 * @param {string} organizationId - Organization UUID
 * @returns {Promise} Organization subscription details
 */
export const getOrganizationSubscriptionDetails = async (organizationId) => {
  try {
    const response = await axiosInstance.post(
      `/organization/subscription/details`,
      { organizationId }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get individual subscription details
 *
 * @param {string} token - Authentication token
 * @param {string} subscriptionId - Stripe Subscription ID
 * @returns {Promise} Individual subscription details
 */
export const getIndividualSubscriptionDetails = async (token, subscriptionId) => {
  try {
    const response = await axiosInstance.post(
      `/subscription`,
      { subscriptionId },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get available subscription plans and prices
 *
 * @returns {Promise} Available products and prices
 */
export const getProductsAndPrices = async () => {
  try {
    const response = await axiosInstance.post(`/prices`, {
      metadataKey: 'type',
      metadataValues: ['44809', 'part107']
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};
